import React, { useState, createContext, useContext } from 'react';

// Import translation files
import enLanding from '../locales/en/landing.json';
import esLanding from '../locales/es/landing.json';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  // The translation files are bundled with the app (imported above), so pick
  // the active one synchronously. Loading them in an effect left the first
  // render with no translations, which flashed raw keys (e.g. "login.loggingIn")
  // and logged "Translation key not found" on every page load.
  const translations = lang === 'es' ? esLanding : enLanding;

  // Translation function - supports nested keys like "landing.hero.title"
  const t = (key, params = {}) => {
    if (!key) return '';
    
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to key if translation not found
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }
    
    // Replace parameters in the translation string
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }
    
    return value || key;
  };

  // Change language
  const changeLanguage = (newLang) => {
    if (newLang === lang) return;
    
    localStorage.setItem('lang', newLang);
    setLang(newLang);
  };

  const value = {
    lang,
    t,
    changeLanguage,
    setLanguage: changeLanguage, // Alias for compatibility
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};
